import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast, Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import StatusBadge from "@/components/StatusBadge";
import { ShieldCheck, CreditCard, AlertTriangle, Clock, CheckCircle2, RefreshCw, ExternalLink, Info, Zap, Phone, MapPin } from "lucide-react";

// Copy for each of the 6 Payments & Payouts states surfaced from the backend.
const CONNECT_STATE_UI = {
  not_connected: {
    label: "Not connected",
    tone: "bg-slate-100 text-slate-800 border-slate-200",
    dot: "bg-slate-400",
    icon: CreditCard,
    headline: "Get paid for FixNear jobs",
    blurb: "Set up secure payouts through Stripe. You'll receive 85% of each completed booking straight to your bank — no invoicing, no chasing.",
    ctaLabel: "Set up payouts with Stripe",
    ctaKind: "primary",
  },
  setup_incomplete: {
    label: "Setup incomplete",
    tone: "bg-amber-50 text-amber-900 border-amber-200",
    dot: "bg-amber-500",
    icon: AlertTriangle,
    headline: "Finish your Stripe onboarding",
    blurb: "You started Stripe onboarding but haven't submitted all the required details yet. Continue to finish and unlock payouts.",
    ctaLabel: "Continue Stripe onboarding",
    ctaKind: "primary",
  },
  verification_required: {
    label: "Verification required",
    tone: "bg-rose-50 text-rose-900 border-rose-200",
    dot: "bg-rose-500",
    icon: AlertTriangle,
    headline: "Stripe needs more information",
    blurb: "Payouts are on hold until you provide the requested information. Click below to open Stripe and finish verification.",
    ctaLabel: "Continue Stripe verification",
    ctaKind: "primary",
  },
  under_review: {
    label: "Under review",
    tone: "bg-blue-50 text-blue-900 border-blue-200",
    dot: "bg-blue-500",
    icon: Clock,
    headline: "Stripe is reviewing your details",
    blurb: "You've submitted everything — Stripe usually confirms within a few minutes to 24 hours. We'll refresh your status automatically.",
    ctaLabel: "Refresh status",
    ctaKind: "outline",
  },
  payments_enabled: {
    label: "Payments enabled",
    tone: "bg-emerald-50 text-emerald-900 border-emerald-200",
    dot: "bg-emerald-500",
    icon: CheckCircle2,
    headline: "You can receive payments",
    blurb: "Stripe has enabled your account. Small extra requirements may be needed later to keep payouts running — please provide them when prompted.",
    ctaLabel: "Update Stripe information",
    ctaKind: "outline",
  },
  payouts_enabled: {
    label: "Payouts enabled",
    tone: "bg-emerald-100 text-emerald-900 border-emerald-300",
    dot: "bg-emerald-600",
    icon: ShieldCheck,
    headline: "Stripe Connect is fully live",
    blurb: "You'll receive 85% of every paid FixNear booking directly to your bank account. No further action needed.",
    ctaLabel: "Payouts enabled ✓",
    ctaKind: "done",
  },
};

// Turn Stripe's raw requirement keys ("individual.dob.day") into readable prompts.
const humaniseRequirement = (key) => {
  const map = {
    "individual.dob.day": "Date of birth",
    "individual.dob.month": "Date of birth",
    "individual.dob.year": "Date of birth",
    "individual.first_name": "First name",
    "individual.last_name": "Last name",
    "individual.address.line1": "Home address",
    "individual.address.postal_code": "Home postcode",
    "individual.address.city": "Home city",
    "individual.email": "Email address",
    "individual.phone": "Phone number",
    "individual.id_number": "ID number",
    "individual.verification.document": "Government ID document",
    "external_account": "Bank account for payouts",
    "tos_acceptance.date": "Accept Stripe terms of service",
    "business_profile.url": "Business website",
    "business_profile.mcc": "Business category",
  };
  return map[key] || key.replaceAll(".", " · ").replaceAll("_", " ");
};

export default function ProviderDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [cats, setCats] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [connectState, setConnectState] = useState({ state: "not_connected", currently_due: [], commission_pct: 15 });
  const [dispatchedLeads, setDispatchedLeads] = useState([]);
  const [leadBusy, setLeadBusy] = useState(null);
  const [quoteDraft, setQuoteDraft] = useState({}); // { [leadId]: { amount, eta, message } }
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [form, setForm] = useState({ business_name: "", bio: "", categories: [], postcodes: "", hourly_rate: "", photo_url: "" });

  const load = async () => {
    const [p, c, b, cs, dl] = await Promise.all([
      api.get("/providers/me/profile"),
      api.get("/categories"),
      api.get("/bookings"),
      api.get("/providers/me/connect/state").catch(() => ({ data: { state: "not_connected", currently_due: [], commission_pct: 15 } })),
      api.get("/providers/me/leads").catch(() => ({ data: [] })),
    ]);
    setProfile(p.data); setCats(c.data); setBookings(b.data); setConnectState(cs.data); setDispatchedLeads(dl.data || []);
    if (p.data) setForm({
      business_name: p.data.business_name || "",
      bio: p.data.bio || "",
      categories: p.data.categories || [],
      postcodes: (p.data.postcodes || []).join(", "),
      hourly_rate: p.data.hourly_rate || "",
      photo_url: p.data.photo_url || "",
    });
  };
  useEffect(() => { load(); }, []);

  const acceptLead = async (leadId) => {
    const etaRaw = window.prompt("Estimated arrival time (minutes)? Leave blank if unknown.");
    if (etaRaw === null) return;
    const eta = etaRaw.trim() ? parseInt(etaRaw, 10) : null;
    setLeadBusy(leadId);
    try {
      await api.post(`/leads/${leadId}/accept`, eta ? { eta_minutes: eta } : {});
      toast.success("Lead accepted — customer notified");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept");
    } finally { setLeadBusy(null); }
  };

  const submitQuote = async (leadId) => {
    const q = quoteDraft[leadId] || {};
    const amount = parseFloat(q.amount || "");
    if (!amount || amount <= 0) return toast.error("Enter a quote amount in £");
    setLeadBusy(leadId);
    try {
      await api.post(`/leads/${leadId}/quote`, {
        amount,
        eta_minutes: q.eta ? parseInt(q.eta, 10) : null,
        message: (q.message || "").trim(),
      });
      toast.success("Quote sent — the customer can now pick you");
      setQuoteDraft((d) => ({ ...d, [leadId]: { amount: "", eta: "", message: "" } }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send quote");
    } finally { setLeadBusy(null); }
  };

  const declineLead = async (leadId) => {
    setLeadBusy(leadId);
    try {
      await api.post(`/leads/${leadId}/decline`);
      toast.success("Declined");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not decline");
    } finally { setLeadBusy(null); }
  };

  const updateLeadStatus = async (leadId, status) => {
    setLeadBusy(leadId);
    try {
      await api.post(`/leads/${leadId}/provider-status`, { status });
      toast.success(status === "on_the_way" ? "Customer notified you're on the way" : "Marked completed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setLeadBusy(null); }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      business_name: form.business_name,
      bio: form.bio,
      categories: form.categories,
      postcodes: form.postcodes.split(",").map((s) => s.trim()).filter(Boolean),
      hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
      photo_url: form.photo_url || null,
    };
    await api.put("/providers/me/profile", payload);
    toast.success("Profile updated");
    load();
  };

  const toggleCat = (slug) => {
    setForm((f) => ({ ...f, categories: f.categories.includes(slug) ? f.categories.filter((s) => s !== slug) : [...f.categories, slug] }));
  };

  const startConnect = async () => {
    // Open the new tab SYNCHRONOUSLY inside the click handler so mobile Safari
    // and the Emergent Preview iframe both allow it (popup blockers require it
    // not to be behind an await). Fill it with a friendly placeholder while the
    // backend generates the Stripe URL, then redirect that same tab.
    const stripeTab = window.open("", "_blank");
    if (stripeTab && !stripeTab.closed) {
      try {
        stripeTab.document.write(
          '<title>Opening Stripe…</title><style>body{font:16px system-ui;padding:40px;color:#0f172a;text-align:center}</style><p>Opening Stripe onboarding…</p>'
        );
      } catch (_) { /* cross-origin write blocked — harmless */ }
    }

    setConnecting(true);
    try {
      const url = `${window.location.origin}/provider`;
      const { data } = await api.post("/providers/me/connect/onboard", { return_url: url, refresh_url: url });
      const dest = data.url;
      if (stripeTab && !stripeTab.closed) {
        stripeTab.location.href = dest;
      } else {
        // Popup blocked — break out of the Preview iframe if we're in one,
        // otherwise navigate the current tab. Never rely on window.location.href
        // alone (Stripe refuses to load inside an iframe → blank page).
        try {
          if (window.top && window.top !== window.self) {
            window.top.location.href = dest;
          } else {
            window.location.href = dest;
          }
        } catch (_) {
          // Cross-origin top access blocked — fall back to same-tab nav.
          window.location.href = dest;
        }
      }
    } catch (e) {
      if (stripeTab && !stripeTab.closed) { try { stripeTab.close(); } catch (_) {} }
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not start Stripe onboarding";
      toast.error("Stripe onboarding could not start", {
        description: msg,
        duration: 15000,
        style: { whiteSpace: "pre-line" },
      });
    } finally { setConnecting(false); }
  };

  const refreshStatus = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/providers/me/connect/state");
      setConnectState(data);
      toast.success("Stripe status refreshed");
    } catch (e) {
      toast.error("Could not refresh — try again shortly");
    } finally { setRefreshing(false); }
  };

  const earnings = bookings
    .filter((b) => b.payment_status === "paid")
    .reduce((s, b) => s + (b.provider_payout || 0), 0);
  const pending = bookings.filter((b) => b.status === "requested").length;

  const ui = CONNECT_STATE_UI[connectState.state] || CONNECT_STATE_UI.not_connected;
  const Icon = ui.icon;
  const commissionPct = connectState.commission_pct || 15;
  const providerPct = 100 - commissionPct;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Toaster position="top-center" />
      <div className="max-w-5xl mx-auto px-4 py-8 grid md:grid-cols-3 gap-6">
        {/* KPI cards */}
        <div className="card">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Earnings</div>
          <div className="text-3xl font-display font-bold mt-1">£{earnings.toFixed(2)}</div>
          <div className="text-xs text-slate-500 mt-1">Net after {commissionPct}% commission</div>
        </div>
        <div className="card">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Pending requests</div>
          <div className="text-3xl font-display font-bold mt-1">{pending}</div>
        </div>
        <div className="card">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Rating</div>
          <div className="text-3xl font-display font-bold mt-1">{profile?.rating_avg?.toFixed?.(1) || "—"}</div>
          <div className="text-xs text-slate-500 mt-1">{profile?.review_count || 0} reviews · {profile?.verified ? <span className="text-emerald-600 inline-flex items-center gap-1"><ShieldCheck size={12} /> Admin-verified</span> : "Awaiting admin verification"}</div>
        </div>

        {/* Available leads dispatched to this provider */}
        <div data-testid="dispatched-leads-section" className="md:col-span-3 card space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <Zap size={18} className="text-rose-600" /> Available requests near you
            </h2>
            <button onClick={load} className="text-xs text-emerald-600 font-semibold" data-testid="refresh-leads">Refresh</button>
          </div>
          {dispatchedLeads.length === 0 ? (
            <div className="text-sm text-slate-500">No new requests dispatched to you right now. Make sure your service categories and postcodes are up to date below.</div>
          ) : (
            <div className="space-y-2">
              {dispatchedLeads.map((row) => {
                const l = row.lead || {};
                const mine = l.assigned_provider_id && (row.status === "selected" || row.status === "accepted");
                const takenByOther = l.assigned_provider_id && !mine;
                const alreadyQuoted = row.status === "quoted";
                const notSelected = row.status === "not_selected";
                const draft = quoteDraft[l.id] || { amount: "", eta: "", message: "" };
                const setDraft = (k, v) => setQuoteDraft((d) => ({ ...d, [l.id]: { ...draft, [k]: v } }));
                return (
                  <div key={row.id} data-testid={`dispatched-lead-${l.id}`}
                    className={`border rounded-xl p-3 ${l.urgent ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-white"}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-xs">
                          {l.urgent && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-600 text-white font-bold"><AlertTriangle size={11}/> URGENT</span>}
                          <span className="text-slate-500">{l.ref}</span>
                          <span className="text-slate-500">·</span>
                          <span className="text-slate-500">{new Date(l.created_at).toLocaleString()}</span>
                        </div>
                        <div className="font-semibold text-slate-900 mt-1">{l.category}</div>
                        <div className="text-sm text-slate-700 mt-1">{l.description}</div>
                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-3">
                          <span className="inline-flex items-center gap-1"><MapPin size={12}/> {l.postcode}</span>
                          <span>When: {l.when_needed}</span>
                        </div>
                        {mine && l.phone && (
                          <div className="text-xs text-slate-700 mt-2 border-t pt-2">
                            Customer: <b>{l.name}</b> · <a href={`tel:${l.phone}`} className="text-emerald-700 font-semibold inline-flex items-center gap-1"><Phone size={12}/>{l.phone}</a>
                          </div>
                        )}
                        {mine && !l.phone && (
                          <div className="text-xs text-slate-500 italic mt-2 border-t pt-2">Customer contact will be released as soon as their payment clears.</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Platform commission</div>
                        <div className={`text-lg font-bold ${l.urgent ? "text-rose-700" : "text-slate-900"}`}>{row.commission_pct || l.commission_pct}%</div>
                        <div className="text-[10px] text-slate-500">You keep {100 - (row.commission_pct || l.commission_pct || 15)}%</div>
                      </div>
                    </div>

                    {/* Quote submission (new) — shown until the customer has picked a provider. */}
                    {row.status === "pending" && !l.assigned_provider_id && (
                      <div className="mt-3 border-t pt-3">
                        <div className="grid sm:grid-cols-3 gap-2">
                          <div>
                            <label className="label text-xs">Your quote (£)</label>
                            <input
                              data-testid={`quote-amount-${l.id}`}
                              type="number" min="1" step="1"
                              value={draft.amount}
                              onChange={(e) => setDraft("amount", e.target.value)}
                              className="field" placeholder="e.g. 120"
                            />
                          </div>
                          <div>
                            <label className="label text-xs">ETA (minutes)</label>
                            <input
                              data-testid={`quote-eta-${l.id}`}
                              type="number" min="1" step="1"
                              value={draft.eta}
                              onChange={(e) => setDraft("eta", e.target.value)}
                              className="field" placeholder="e.g. 45"
                            />
                          </div>
                          <div>
                            <label className="label text-xs">Message to customer</label>
                            <input
                              data-testid={`quote-message-${l.id}`}
                              value={draft.message}
                              onChange={(e) => setDraft("message", e.target.value)}
                              className="field" placeholder="Short note (optional)"
                              maxLength={200}
                            />
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button data-testid={`quote-send-${l.id}`} disabled={leadBusy===l.id} onClick={() => submitQuote(l.id)} className="btn-accent text-sm">Send quote</button>
                          <button data-testid={`lead-decline-${l.id}`} disabled={leadBusy===l.id} onClick={() => declineLead(l.id)} className="btn-outline text-sm">Not interested</button>
                        </div>
                      </div>
                    )}

                    {alreadyQuoted && !mine && (
                      <div className="mt-3 border-t pt-3 text-xs text-emerald-700 font-semibold">
                        Quote sent — waiting for the customer to pick a provider.
                      </div>
                    )}

                    {notSelected && (
                      <div className="mt-3 border-t pt-3 text-xs text-slate-500 italic">
                        The customer picked another provider this time.
                      </div>
                    )}

                    <div className="mt-3 flex flex-wrap gap-2">
                      {mine && l.status === "provider_selected" && (
                        <span className="text-xs text-emerald-700 font-semibold">🎉 You've been selected — waiting for payment</span>
                      )}
                      {mine && l.status === "payment_confirmed" && (
                        <button data-testid={`lead-otw-${l.id}`} disabled={leadBusy===l.id} onClick={() => updateLeadStatus(l.id, "on_the_way")} className="btn-accent text-sm">I'm on the way</button>
                      )}
                      {mine && (l.status === "accepted" || l.status === "on_the_way") && l.status !== "payment_confirmed" && l.phone && (
                        <button data-testid={`lead-otw-${l.id}`} disabled={leadBusy===l.id} onClick={() => updateLeadStatus(l.id, "on_the_way")} className="btn-accent text-sm">I'm on the way</button>
                      )}
                      {mine && l.status === "on_the_way" && (
                        <button data-testid={`lead-done-${l.id}`} disabled={leadBusy===l.id} onClick={() => updateLeadStatus(l.id, "completed")} className="btn-primary text-sm">Mark completed</button>
                      )}
                      {takenByOther && <span className="text-xs text-slate-500 italic">The customer picked another provider.</span>}
                      {row.status === "declined" && <span className="text-xs text-slate-500 italic">Declined</span>}
                      {mine && l.status === "completed" && <span className="text-xs text-emerald-700 font-semibold">Completed ✓</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Business profile form */}
        <form onSubmit={save} className="md:col-span-2 card space-y-4">
          <h2 className="font-display text-xl font-bold">Business profile</h2>
          <div>
            <label className="label">Business name</label>
            <input data-testid="pf-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="field" />
          </div>
          <div>
            <label className="label">Bio</label>
            <textarea data-testid="pf-bio" rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="field" />
          </div>
          <div>
            <label className="label">Services offered</label>
            <div className="flex gap-2 flex-wrap">
              {cats.map((c) => (
                <button type="button" key={c.slug} data-testid={`pf-cat-${c.slug}`} onClick={() => toggleCat(c.slug)}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${form.categories.includes(c.slug) ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-700 border-slate-200"}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">Service postcodes (comma separated)</label>
            <input data-testid="pf-postcodes" value={form.postcodes} onChange={(e) => setForm({ ...form, postcodes: e.target.value })} className="field" placeholder="L1, L2, L3" />
          </div>
          <div>
            <label className="label">Hourly rate (£)</label>
            <input data-testid="pf-rate" type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="field" />
          </div>
          <button data-testid="pf-save" className="btn-accent">Save profile</button>
        </form>

        {/* Job requests */}
        <div className="md:col-span-3">
          <h2 className="font-display text-xl font-bold mb-3">Job requests</h2>
          <div className="space-y-3">
            {bookings.length === 0 && <div className="card text-slate-600">No requests yet.</div>}
            {bookings.map((b) => (
              <Link key={b.id} to={`/bookings/${b.id}`} data-testid={`pf-booking-${b.id}`} className="card flex items-center justify-between hover:-translate-y-1 transition-transform">
                <div>
                  <div className="font-semibold">{b.customer_name}</div>
                  <div className="text-sm text-slate-600 line-clamp-1">{b.description}</div>
                  <div className="text-xs text-slate-500 mt-1">{b.preferred_date} · {b.postcode}</div>
                </div>
                <StatusBadge status={b.status} />
              </Link>
            ))}
          </div>
        </div>

        {/* ---------- Payments & Payouts (new section, placed below existing content) ---------- */}
        <div className="md:col-span-3" data-testid="payments-payouts-section">
          <div className="flex items-center gap-3 mb-3">
            <h2 className="font-display text-xl font-bold">Payments &amp; Payouts</h2>
            <span
              data-testid="connect-state-badge"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold border ${ui.tone}`}
            >
              <span className={`w-2 h-2 rounded-full ${ui.dot}`} />
              {ui.label}
            </span>
          </div>

          <div className={`card !p-6 border ${ui.tone.replace("bg-", "bg-opacity-30 bg-").split(" ")[0]}`}>
            <div className="flex items-start gap-4 flex-col sm:flex-row">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/70 border border-slate-200 shrink-0">
                <Icon size={22} className="text-slate-700" />
              </div>

              <div className="flex-1">
                <div className="font-display text-lg font-bold text-slate-900">{ui.headline}</div>
                <p className="text-sm text-slate-700 mt-1">{ui.blurb}</p>

                {/* Show the specific requirements Stripe is waiting on */}
                {connectState.currently_due && connectState.currently_due.length > 0 && (
                  <div data-testid="connect-requirements" className="mt-3 rounded-lg bg-white/70 border border-slate-200 p-3">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-600 mb-2">Stripe is asking for</div>
                    <ul className="text-sm text-slate-800 space-y-1">
                      {connectState.currently_due.map((k) => (
                        <li key={k} className="flex items-start gap-2">
                          <span className="text-rose-500 mt-0.5">•</span>
                          <span>{humaniseRequirement(k)} <code className="text-[10px] text-slate-500 ml-1">{k}</code></span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {connectState.disabled_reason && (
                  <div className="mt-3 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-lg p-2">
                    <b>Payouts blocked:</b> {connectState.disabled_reason}
                  </div>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {ui.ctaKind === "primary" && (
                    <button
                      data-testid="connect-stripe-btn"
                      onClick={startConnect}
                      disabled={connecting}
                      className="btn-accent"
                    >
                      {connecting ? "Redirecting to Stripe…" : (
                        <span className="inline-flex items-center gap-2">{ui.ctaLabel} <ExternalLink size={14} /></span>
                      )}
                    </button>
                  )}
                  {ui.ctaKind === "outline" && (
                    <>
                      <button
                        data-testid="refresh-stripe-status-btn"
                        onClick={refreshStatus}
                        disabled={refreshing}
                        className="btn-outline"
                      >
                        <span className="inline-flex items-center gap-2">
                          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : ui.ctaLabel}
                        </span>
                      </button>
                      {connectState.state === "payments_enabled" && (
                        <button
                          data-testid="update-stripe-info-btn"
                          onClick={startConnect}
                          disabled={connecting}
                          className="btn-accent"
                        >
                          {connecting ? "Redirecting…" : (<span className="inline-flex items-center gap-2">Update on Stripe <ExternalLink size={14} /></span>)}
                        </button>
                      )}
                    </>
                  )}
                  {ui.ctaKind === "done" && (
                    <button data-testid="connect-stripe-btn" disabled className="btn-outline !opacity-100 !cursor-default">
                      <span className="inline-flex items-center gap-2 text-emerald-700">
                        <CheckCircle2 size={16} /> {ui.ctaLabel}
                      </span>
                    </button>
                  )}
                  {connectState.stripe_account_id && (
                    <span className="text-xs text-slate-500">Connected account: <code>{connectState.stripe_account_id}</code></span>
                  )}
                </div>
              </div>
            </div>

            {/* Commission explainer */}
            <div className="mt-5 pt-4 border-t border-slate-200 flex items-start gap-2 text-xs text-slate-600">
              <Info size={14} className="mt-0.5 shrink-0" />
              <div>
                FixNear takes a <b>{commissionPct}%</b> platform commission on each completed booking. You receive <b>{providerPct}%</b> net of Stripe fees, paid directly to your bank. Payouts are only released once Stripe has confirmed your account is fully verified.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
